export type RefreshSkipReason = "SIGNED" | "NO_PLACEHOLDER_IN_SCOPE";

export interface RefreshCertificatesResult {
  refreshed?: { updatedPlaceholders?: number }[];
  skipped?: { reason?: RefreshSkipReason | string }[];
}

export type RefreshOutcomeKind =
  | "updated"
  | "already-updated"
  | "none-updated"
  | "partial"
  | "nothing-to-update"
  | "started";

export interface RefreshOutcome {
  kind: RefreshOutcomeKind;
  updated: number;
  alreadyUpdated: number;
  signed: number;
  noPlaceholder: number;
  other: number;
}

export const getRefreshOutcome = (
  result: RefreshCertificatesResult | null | undefined,
): RefreshOutcome => {
  const refreshedRaw = result?.refreshed;
  const skippedRaw = result?.skipped;
  const refreshed = Array.isArray(refreshedRaw) ? refreshedRaw : null;
  const skipped = Array.isArray(skippedRaw) ? skippedRaw : null;

  const updated = (refreshed ?? []).filter(
    (item) => (item?.updatedPlaceholders ?? 0) > 0,
  ).length;
  const alreadyUpdated = (refreshed?.length ?? 0) - updated;
  let signed = 0;
  let noPlaceholder = 0;
  let other = 0;
  (skipped ?? []).forEach((item) => {
    if (item?.reason === "SIGNED") signed += 1;
    else if (item?.reason === "NO_PLACEHOLDER_IN_SCOPE") noPlaceholder += 1;
    else other += 1;
  });
  const counts = { updated, alreadyUpdated, signed, noPlaceholder, other };

  if (!refreshed && !skipped) return { kind: "started", ...counts };
  const refreshedCount = refreshed?.length ?? 0;
  const skippedCount = skipped?.length ?? 0;
  if (refreshedCount === 0 && skippedCount === 0) {
    return { kind: "nothing-to-update", ...counts };
  }
  if (skippedCount === 0) {
    return { kind: updated > 0 ? "updated" : "already-updated", ...counts };
  }
  if (refreshedCount === 0) return { kind: "none-updated", ...counts };
  return { kind: "partial", ...counts };
};

export interface RefreshResultLine {
  key: string;
  count?: number;
}

export const getRefreshResultLines = (
  o: RefreshOutcome,
): RefreshResultLine[] => {
  if (o.kind === "started") return [{ key: "started" }];
  if (o.kind === "nothing-to-update") return [{ key: "nothing-to-update" }];
  const skippedPrefix = o.kind === "none-updated" ? "none" : "partial";
  const lines: RefreshResultLine[] = [];
  if (o.updated > 0) lines.push({ key: "updated-count", count: o.updated });
  if (o.alreadyUpdated > 0) {
    lines.push({ key: "already-updated-count", count: o.alreadyUpdated });
  }
  if (o.signed > 0) {
    lines.push({ key: `${skippedPrefix}-signed-count`, count: o.signed });
  }
  if (o.noPlaceholder > 0) {
    lines.push({
      key: `${skippedPrefix}-no-placeholder-count`,
      count: o.noPlaceholder,
    });
  }
  if (o.other > 0) lines.push({ key: "skipped-other-count", count: o.other });
  return lines;
};

export const getRefreshResultTitleKey = (o: RefreshOutcome): string => {
  const nothingUpdated =
    o.kind === "none-updated" || (o.kind === "partial" && o.updated === 0);
  if (nothingUpdated) return "none-updated-title";
  if (o.kind === "started") return "started-title";
  if (o.kind === "updated") return "success-title";
  return "completed-title";
};
